import { useState } from 'react';
import { Loader2, Play } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '@travellegacy/nexops-design-system';
import { Input } from '@travellegacy/nexops-design-system';
import { Button } from '@travellegacy/nexops-design-system';
import { toast } from '@/shared/lib/toast';

interface AddCollaboratorFormData {
  phoneNumber: string;
  instanceToken: string;
}

interface OperationResult {
  totalProcessed: number;
  successCount: number;
  errorCount: number;
  collaboratorsAdded: number;
  collaboratorsPromoted: number;
  alreadyInGroup: number;
  errors?: Array<{
    clientName: string;
    error: string;
  }>;
}

interface ProgressInfo {
  current: number;
  total: number;
  currentClientName: string;
}

interface AddCollaboratorsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function AddCollaboratorsModal({ isOpen, onClose }: AddCollaboratorsModalProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [operationResults, setOperationResults] = useState<OperationResult | null>(null);
  const [progressInfo, setProgressInfo] = useState<ProgressInfo | null>(null);
  const [formData, setFormData] = useState<AddCollaboratorFormData>({
    phoneNumber: '',
    instanceToken: '',
  });

  const handleClose = () => {
    onClose();
    setFormData({ phoneNumber: '', instanceToken: '' });
    setShowResults(false);
    setOperationResults(null);
    setProgressInfo(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const executeOperation = async () => {
    if (!formData.phoneNumber || !formData.instanceToken) {
      toast.error('Erro', 'Preencha todos os campos');
      return;
    }

    setIsLoading(true);
    setShowResults(true);

    // Inicializar estatísticas
    setOperationResults({
      totalProcessed: 0,
      successCount: 0,
      errorCount: 0,
      collaboratorsAdded: 0,
      collaboratorsPromoted: 0,
      alreadyInGroup: 0,
    });

    try {
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
      const url = `${API_URL}/operations/add-collaborators/stream?phoneNumber=${encodeURIComponent(formData.phoneNumber)}&instanceToken=${encodeURIComponent(formData.instanceToken)}`;

      const eventSource = new EventSource(url, { withCredentials: true });
      let hasReceivedData = false;

      eventSource.onmessage = (event) => {
        try {
          hasReceivedData = true;
          const data = JSON.parse(event.data);

          if (data.type === 'init') {
            // Operação iniciada
          } else if (data.type === 'filtered') {
            setProgressInfo({
              current: 0,
              total: data.totalActive,
              currentClientName: '',
            });
          } else if (data.type === 'progress') {
            // Atualizar progresso
            setProgressInfo({
              current: data.current,
              total: data.total,
              currentClientName: data.clientName,
            });

            // Atualizar estatísticas em tempo real
            setOperationResults({
              totalProcessed: data.stats.totalProcessed,
              successCount: data.stats.successCount,
              errorCount: data.stats.errorCount,
              collaboratorsAdded: data.stats.collaboratorsAdded,
              collaboratorsPromoted: data.stats.collaboratorsPromoted,
              alreadyInGroup: data.stats.alreadyInGroup,
            });
          } else if (data.type === 'complete') {
            // Resultado final
            setOperationResults(data.result);
            setIsLoading(false);
            eventSource.close();
            toast.success('Sucesso', 'Operação concluída com sucesso!');
          } else if (data.type === 'error') {
            setIsLoading(false);
            eventSource.close();
            toast.error('Erro', data.error || 'Erro ao executar operação');
          }
        } catch (err) {
          console.error('Erro ao processar evento SSE:', err);
        }
      };

      eventSource.onerror = () => {
        if (!hasReceivedData) {
          toast.error(
            'Erro',
            'Não foi possível conectar ao servidor. Verifique se a API está rodando.'
          );
        } else {
          toast.error('Erro', 'Conexão interrompida com o servidor');
        }

        setIsLoading(false);
        eventSource.close();
      };
    } catch (error) {
      console.error('Erro ao executar operação:', error);
      setIsLoading(false);
      toast.error('Erro', 'Erro ao executar operação. Verifique os logs.');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogClose onClick={handleClose} />
        <DialogHeader>
          <DialogTitle>Adicionar Colaboradores aos Grupos</DialogTitle>
          <DialogDescription>
            Esta operação irá adicionar o colaborador em todos os grupos de clientes ativos e
            promovê-lo a administrador
          </DialogDescription>
        </DialogHeader>

        <div className="p-6 space-y-4">
          {!isLoading && !showResults ? (
            <>
              <div>
                <label
                  htmlFor="phoneNumber"
                  className="block text-sm font-medium text-foreground mb-2"
                >
                  Número de Telefone do Colaborador
                </label>
                <Input
                  id="phoneNumber"
                  name="phoneNumber"
                  type="text"
                  placeholder="5512988776655"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  disabled={isLoading}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Formato: DDI + DDD + Número (ex: 5512988776655)
                </p>
              </div>

              <div>
                <label
                  htmlFor="instanceToken"
                  className="block text-sm font-medium text-foreground mb-2"
                >
                  Token da Instância UAZAPI
                </label>
                <Input
                  id="instanceToken"
                  name="instanceToken"
                  type="text"
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  value={formData.instanceToken}
                  onChange={handleInputChange}
                  disabled={isLoading}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Token da conta do WhatsApp do colaborador
                </p>
              </div>

              <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">
                  O que esta operação faz:
                </h4>
                <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1 list-disc list-inside">
                  <li>Busca todos os clientes ativos no Notion com GroupInvite preenchido</li>
                  <li>Verifica se o colaborador já está no grupo usando OPERACIONAL_ROBO</li>
                  <li>Adiciona o colaborador nos grupos onde ele ainda não está presente</li>
                  <li>Promove o colaborador a administrador do grupo</li>
                  <li>Exibe estatísticas em tempo real durante o processo</li>
                </ul>
              </div>
            </>
          ) : showResults ? (
            <div className="space-y-4">
              {/* Barra de Progresso */}
              {isLoading && progressInfo && (
                <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4 space-y-3">
                  <div className="flex justify-between items-center">
                    <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100">
                      Processando...
                    </h4>
                    <span className="text-sm text-blue-700 dark:text-blue-300">
                      {progressInfo.current} de {progressInfo.total}
                    </span>
                  </div>

                  {/* Barra de progresso */}
                  <div className="w-full bg-blue-200 dark:bg-blue-900 rounded-full h-2.5">
                    <div
                      className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                      style={{ width: `${(progressInfo.current / progressInfo.total) * 100}%` }}
                    ></div>
                  </div>

                  {progressInfo.currentClientName && (
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      Processando: <strong>{progressInfo.currentClientName}</strong>
                    </p>
                  )}
                </div>
              )}

              {/* Estatísticas em Tempo Real */}
              <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg p-4">
                <h4 className="text-lg font-semibold text-green-900 dark:text-green-100 mb-3">
                  {isLoading ? 'Processando...' : 'Operação Concluída!'}
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white dark:bg-green-900/20 rounded p-3">
                    <p className="text-sm text-green-700 dark:text-green-300">Total Processado</p>
                    <p className="text-2xl font-bold text-green-900 dark:text-green-100">
                      {operationResults?.totalProcessed || 0}
                    </p>
                  </div>
                  <div className="bg-white dark:bg-green-900/20 rounded p-3">
                    <p className="text-sm text-green-700 dark:text-green-300">Adicionados</p>
                    <p className="text-2xl font-bold text-green-900 dark:text-green-100">
                      {operationResults?.collaboratorsAdded || 0}
                    </p>
                  </div>
                  <div className="bg-white dark:bg-green-900/20 rounded p-3">
                    <p className="text-sm text-green-700 dark:text-green-300">Promovidos a Admin</p>
                    <p className="text-2xl font-bold text-green-900 dark:text-green-100">
                      {operationResults?.collaboratorsPromoted || 0}
                    </p>
                  </div>
                  <div className="bg-white dark:bg-green-900/20 rounded p-3">
                    <p className="text-sm text-green-700 dark:text-green-300">Já no Grupo</p>
                    <p className="text-2xl font-bold text-green-900 dark:text-green-100">
                      {operationResults?.alreadyInGroup || 0}
                    </p>
                  </div>
                  <div className="bg-white dark:bg-green-900/20 rounded p-3 col-span-2">
                    <p className="text-sm text-green-700 dark:text-green-300">Erros</p>
                    <p className="text-2xl font-bold text-red-900 dark:text-red-100">
                      {operationResults?.errorCount || 0}
                    </p>
                  </div>
                </div>
              </div>

              {operationResults?.errors && operationResults.errors.length > 0 && (
                <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg p-4 max-h-60 overflow-y-auto">
                  <h4 className="text-sm font-semibold text-red-900 dark:text-red-100 mb-2">
                    Clientes com Erro:
                  </h4>
                  <ul className="space-y-2">
                    {operationResults.errors.map((error, index) => (
                      <li key={index} className="text-sm text-red-700 dark:text-red-300">
                        <strong>{error.clientName}:</strong> {error.error}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : null}
        </div>

        <DialogFooter>
          {!isLoading && !showResults ? (
            <>
              <Button variant="outline" onClick={handleClose} disabled={isLoading}>
                Cancelar
              </Button>
              <Button onClick={executeOperation} disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Processando...
                  </>
                ) : (
                  <>
                    <Play className="mr-2 h-4 w-4" />
                    Executar
                  </>
                )}
              </Button>
            </>
          ) : showResults ? (
            <>{!isLoading && <Button onClick={handleClose}>Fechar</Button>}</>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
